import React from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { Toaster } from "sonner";
import "./lib/i18n";
import { store } from "./store";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import Register from "./pages/Register";
import VerifyContact from "./pages/VerifyContact";
import AndroidMapPreview from "./pages/AndroidMapPreview";
import LegalPage from "./pages/LegalPage";
import NotificationBridge from "./components/NotificationBridge";
import SplashScreen from "./components/SplashScreen";
import WelcomeMessage from "./components/WelcomeMessage";

import CustomerHome from "./pages/customer/CustomerHome";
import RideBooking from "./pages/customer/RideBooking";
import RideTracking from "./pages/customer/RideTracking";
import Wallet from "./pages/customer/Wallet";
import Profile from "./pages/customer/Profile";
import CustomerServices from "./pages/customer/CustomerServices";
import DeliveryBooking from "./pages/customer/DeliveryBooking";
import Marketplace from "./pages/customer/Marketplace";
import AccountOptionPage from "./pages/customer/AccountOptionPage";
import IdentityVerification from "./pages/customer/IdentityVerification";

import DriverHome from "./pages/driver/DriverHome";
import DriverWallet from "./pages/driver/DriverWallet";
import DriverHistory from "./pages/driver/DriverHistory";
import DriverProfile from "./pages/driver/DriverProfile";
import DriverDocuments from "./pages/driver/DriverDocuments";
import DriverSupport from "./pages/driver/DriverSupport";
import MerchantDashboard from "./pages/merchant/MerchantDashboard";
import AgentDashboard from "./pages/agent/AgentDashboard";

import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminDrivers from "./pages/admin/AdminDrivers";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminRides from "./pages/admin/AdminRides";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSOS from "./pages/admin/AdminSOS";
import AdminDocs from "./pages/admin/AdminDocs";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminAds from "./pages/admin/AdminAds";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminFinance from "./pages/admin/AdminFinance";
import AdminDisputes from "./pages/admin/AdminDisputes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminLaunch from "./pages/admin/AdminLaunch";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminMarketplace from "./pages/admin/AdminMarketplace";
import AdminPlatform from "./pages/admin/AdminPlatform";

import "./App.css";

function userAccessSet(user) {
  const modules = new Set(Array.isArray(user?.access_modules) ? user.access_modules : []);
  if (user?.role) modules.add(user.role);
  return modules;
}

function userHasAccess(user, roles) {
  if (!roles?.length) return true;
  const modules = userAccessSet(user);
  return roles.some((role) => modules.has(role));
}

function fallbackRouteForUser(user) {
  const modules = userAccessSet(user);
  if (modules.has("admin")) return "/admin";
  if (modules.has("customer")) return "/app";
  if (modules.has("driver")) return "/driver";
  if (modules.has("courier")) return "/courier";
  if (modules.has("merchant")) return "/merchant";
  if (modules.has("agent")) return "/agent";
  if (modules.has("finance")) return "/admin/finance";
  return "/";
}

function Loading() {
  return (
    <div className="flex min-h-[100svh] items-center justify-center overflow-hidden bg-[#03060d] text-white" data-testid="loading-screen">
      <div className="w-full max-w-xs px-6 text-center">
        <img src="/nexora-cab.png" alt="Nexora Cab" className="mx-auto h-20 w-20 rounded-2xl object-cover shadow-[0_0_44px_rgba(0,212,255,0.34)]" />
        <div className="mt-4 font-heading text-2xl font-black text-white">
        NEXORA-cab
        </div>
        <div className="relative mt-6 h-16 w-full overflow-hidden rounded-full border border-white/10 bg-white/5">
          <div className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
          <div className="splash-car absolute top-1/2 -translate-y-1/2">
            <div className="relative h-12 w-24">
              <div className="absolute bottom-3 left-2 h-8 w-20 rounded-t-[28px] rounded-b-xl bg-gradient-to-r from-primary to-secondary shadow-[0_0_30px_rgba(0,212,255,0.5)]" />
              <div className="absolute bottom-2 left-5 h-4 w-4 rounded-full border-4 border-white/80 bg-black" />
              <div className="absolute bottom-2 right-5 h-4 w-4 rounded-full border-4 border-white/80 bg-black" />
              <div className="absolute bottom-8 left-10 h-4 w-8 rounded-t-xl bg-black/45" />
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-center gap-1.5">
          {[1, 2, 3, 4, 5, 6].map((item) => <span key={item} className="h-2 w-2 rounded-full bg-primary shadow-[0_0_12px_rgba(0,212,255,0.85)]" />)}
        </div>
      </div>
    </div>
  );
}

function Protected({ roles, skipContactVerification = false, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !userHasAccess(user, roles)) {
    return <Navigate to={fallbackRouteForUser(user)} replace />;
  }
  const contactVerified = user.email_verified || user.role === "admin";
  if (!skipContactVerification && !contactVerified) return <Navigate to="/verify-contact" replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Landing />;
  if (user.role !== "admin" && !user.email_verified) return <Navigate to="/verify-contact" replace />;
  const fallback = fallbackRouteForUser(user);
  if (fallback !== "/") return <Navigate to={fallback} replace />;
  return <Landing />;
}

function AppRoutes() {
  const { theme } = useTheme();
  const { user } = useAuth();
  return (
    <>
      <Toaster position="top-center" theme={theme} />
      <SplashScreen />
      <WelcomeMessage user={user} />
      {user && <NotificationBridge />}
      <Routes>
        <Route path="/" element={<RoleRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-contact" element={<Protected skipContactVerification><VerifyContact /></Protected>} />
        <Route path="/android-map" element={<AndroidMapPreview />} />
        <Route path="/privacy" element={<LegalPage type="privacy" />} />
        <Route path="/terms" element={<LegalPage type="terms" />} />

        <Route path="/app" element={<Protected roles={["customer"]}><CustomerHome /></Protected>} />
        <Route path="/app/ride" element={<Protected roles={["customer"]}><RideBooking /></Protected>} />
        <Route path="/app/ride/:id" element={<Protected roles={["customer"]}><RideTracking /></Protected>} />
        <Route path="/app/services" element={<Protected roles={["customer"]}><CustomerServices /></Protected>} />
        <Route path="/app/delivery" element={<Protected roles={["customer"]}><DeliveryBooking /></Protected>} />
        <Route path="/app/marketplace/:type" element={<Protected roles={["customer"]}><Marketplace /></Protected>} />
        <Route path="/app/wallet" element={<Protected roles={["customer"]}><Wallet /></Protected>} />
        <Route path="/app/profile" element={<Protected roles={["customer"]}><Profile /></Protected>} />
        <Route path="/app/account/:section" element={<Protected roles={["customer"]}><AccountOptionPage /></Protected>} />
        <Route path="/app/verify-identity" element={<Protected roles={["customer"]}><IdentityVerification /></Protected>} />
        <Route path="/app/history" element={<Navigate to="/app/profile" replace />} />

        <Route path="/driver" element={<Protected roles={["driver"]}><DriverHome /></Protected>} />
        <Route path="/driver/wallet" element={<Protected roles={["driver"]}><DriverWallet /></Protected>} />
        <Route path="/driver/history" element={<Protected roles={["driver"]}><DriverHistory /></Protected>} />
        <Route path="/driver/profile" element={<Protected roles={["driver"]}><DriverProfile /></Protected>} />
        <Route path="/driver/documents" element={<Protected roles={["driver"]}><DriverDocuments /></Protected>} />
        <Route path="/driver/support" element={<Protected roles={["driver"]}><DriverSupport /></Protected>} />

        <Route path="/courier" element={<Protected roles={["courier"]}><DriverHome /></Protected>} />
        <Route path="/courier/wallet" element={<Protected roles={["courier"]}><DriverWallet /></Protected>} />
        <Route path="/courier/history" element={<Protected roles={["courier"]}><DriverHistory /></Protected>} />
        <Route path="/courier/profile" element={<Protected roles={["courier"]}><DriverProfile /></Protected>} />
        <Route path="/courier/documents" element={<Protected roles={["courier"]}><DriverDocuments /></Protected>} />
        <Route path="/courier/support" element={<Protected roles={["courier"]}><DriverSupport /></Protected>} />

        <Route path="/merchant" element={<Protected roles={["merchant"]}><MerchantDashboard /></Protected>} />
        <Route path="/agent" element={<Protected roles={["agent"]}><AgentDashboard /></Protected>} />

        <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
        <Route path="/admin/overview" element={<Protected roles={["admin"]}><AdminOverview /></Protected>} />
        <Route path="/admin/drivers" element={<Protected roles={["admin", "driver_onboarding"]}><AdminDrivers /></Protected>} />
        <Route path="/admin/users" element={<Protected roles={["admin"]}><AdminUsers /></Protected>} />
        <Route path="/admin/payments" element={<Protected roles={["admin", "finance"]}><AdminPayments /></Protected>} />
        <Route path="/admin/finance" element={<Protected roles={["admin", "finance"]}><AdminFinance /></Protected>} />
        <Route path="/admin/disputes" element={<Protected roles={["admin", "support"]}><AdminDisputes /></Protected>} />
        <Route path="/admin/ads" element={<Protected roles={["admin", "ads"]}><AdminAds /></Protected>} />
        <Route path="/admin/events" element={<Protected roles={["admin", "settings"]}><AdminEvents /></Protected>} />
        <Route path="/admin/marketplace" element={<Protected roles={["admin", "marketplace", "merchant_onboarding"]}><AdminMarketplace /></Protected>} />
        <Route path="/admin/profile" element={<Protected roles={["admin", "finance", "support", "marketplace", "delivery_ops", "driver_onboarding", "merchant_onboarding", "ads", "settings"]}><AdminProfile /></Protected>} />
        <Route path="/admin/pricing" element={<Protected roles={["admin", "settings", "finance"]}><AdminPricing /></Protected>} />
        <Route path="/admin/rides" element={<Protected roles={["admin", "support", "delivery_ops"]}><AdminRides /></Protected>} />
        <Route path="/admin/sos" element={<Protected roles={["admin", "support"]}><AdminSOS /></Protected>} />
        <Route path="/admin/documents" element={<Protected roles={["admin", "driver_onboarding"]}><AdminDocs /></Protected>} />
        <Route path="/admin/audit" element={<Protected roles={["admin"]}><AdminAudit /></Protected>} />
        <Route path="/admin/settings" element={<Protected roles={["admin", "settings"]}><AdminSettings /></Protected>} />
        <Route path="/admin/launch" element={<Protected roles={["admin"]}><AdminLaunch /></Protected>} />
        <Route path="/admin/platform" element={<Protected roles={["admin", "settings"]}><AdminPlatform /></Protected>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Provider>
  );
}
